/**
 * Turn logo.dev's dominant brand colours into a recruiter theme suggestion.
 *
 * The Brand API returns `colors[]` strongest-first with no primary/secondary
 * semantics, so we pick them ourselves under two constraints:
 *
 *   1. The API validates both theme colours with `ContrastWithWhite` (see
 *      floats-api/app/Rules/ContrastWithWhite.php) and rejects anything under
 *      1.5:1 against white. Suggesting a pale brand colour would 422 the save,
 *      so those are filtered out here rather than surfaced and then refused.
 *   2. Secondary has to read as a different colour from primary — a brand whose
 *      top two dominant colours are near-identical shades gets no secondary and
 *      falls back to the design-system default (see lib/helpers/colors.js).
 *
 * The WCAG maths mirrors ContrastWithWhite exactly so the two sides agree.
 */

/** Minimum WCAG contrast ratio against white — must match ContrastWithWhite. */
const MIN_CONTRAST_WITH_WHITE = 1.5;

/**
 * Minimum Euclidean RGB distance between primary and secondary. ~64 keeps
 * obvious shade-of-the-same-colour pairs out while allowing genuinely distinct
 * brand colours through.
 */
const MIN_DISTINCT_DISTANCE = 64;

export interface ThemeColorSuggestion {
  primary: string | null;
  secondary: string | null;
}

type Rgb = [number, number, number];

/** Parse `#rgb` / `#rrggbb` (with or without the hash) into RGB channels. */
export function parseHex(value: string): Rgb | null {
  if (typeof value !== "string") return null;

  let hex = value.trim().replace(/^#/, "");

  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }

  if (!/^[0-9a-fA-F]{6}$/.test(hex)) return null;

  return [
    parseInt(hex.slice(0, 2), 16),
    parseInt(hex.slice(2, 4), 16),
    parseInt(hex.slice(4, 6), 16),
  ];
}

function relativeLuminance([r, g, b]: Rgb): number {
  const channels = [r, g, b].map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });

  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
}

/** WCAG contrast ratio of a colour against a white background. */
export function contrastWithWhite(hex: string): number {
  const rgb = parseHex(hex);
  if (!rgb) return 0;

  return 1.05 / (relativeLuminance(rgb) + 0.05);
}

/** True when the colour is dark enough to be legible on white. */
export function isUsableOnWhite(hex: string): boolean {
  return contrastWithWhite(hex) >= MIN_CONTRAST_WITH_WHITE;
}

function distance(a: Rgb, b: Rgb): number {
  return Math.sqrt((a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2 + (a[2] - b[2]) ** 2);
}

/**
 * Pick theme colours from a dominant-first list of brand hex values.
 *
 * @param hexes Dominant brand colours, strongest first.
 */
export function pickThemeColors(hexes: unknown): ThemeColorSuggestion {
  if (!Array.isArray(hexes)) return { primary: null, secondary: null };

  const usable = hexes.filter(
    (hex): hex is string => typeof hex === "string" && isUsableOnWhite(hex)
  );

  const primary = usable[0] ?? null;
  if (!primary) return { primary: null, secondary: null };

  const primaryRgb = parseHex(primary) as Rgb;
  const secondary =
    usable.slice(1).find((hex) => {
      const rgb = parseHex(hex);
      return rgb !== null && distance(primaryRgb, rgb) >= MIN_DISTINCT_DISTANCE;
    }) ?? null;

  return { primary, secondary };
}
